import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { staffAPI, classesAPI, SITE_NAMES, SITE_COLORS } from '../../lib/api';
import { ArrowLeft, Mail, Phone, User } from 'lucide-react';
import { SEO } from '../seo/SEO';

export const StaffPage = ({ siteKey }) => {
  const [staff, setStaff] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const colors = SITE_COLORS[siteKey];
  const siteName = SITE_NAMES[siteKey];

  useEffect(() => {
    const loadData = async () => {
      try {
        const [staffRes, classesRes] = await Promise.all([
          staffAPI.getAll(siteKey),
          classesAPI.getAll(siteKey)
        ]);
        setStaff(staffRes.data);
        setClasses(classesRes.data);
      } catch (error) {
        console.error('Error loading staff:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [siteKey]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner" />
      </div>
    );
  }

  // Group staff by class
  const staffByClass = {};
  const unassigned = [];
  
  staff.forEach(member => {
    if (member.class_name) {
      if (!staffByClass[member.class_name]) {
        staffByClass[member.class_name] = [];
      }
      staffByClass[member.class_name].push(member);
    } else {
      unassigned.push(member);
    }
  });

  const StaffCard = ({ member, index }) => (
    <div 
      className="bg-white rounded-2xl border-3 border-[#F0F4F8] overflow-hidden hover:shadow-lg transition-all animate-fade-in-up"
      style={{ animationDelay: `${index * 80}ms` }}
      data-testid={`staff-card-${index}`}
    >
      <div className="aspect-[3/4] relative bg-gradient-to-br from-slate-100 to-slate-50">
        {member.photo_url ? (
          <img src={member.photo_url} alt={member.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="w-24 h-24 rounded-full flex items-center justify-center" style={{ backgroundColor: `${colors.primary}15` }}>
              <User size={48} style={{ color: colors.primary }} />
            </div>
          </div>
        )}
      </div>
      <div className="p-5">
        <h3 className="font-bold text-lg text-[#2D3748] mb-1">{member.name}</h3>
        <p className="text-sm font-medium mb-3" style={{ color: colors.primary }}>{member.position}</p>
        
        <div className="space-y-2">
          {member.email && (
            <a href={`mailto:${member.email}`} className="flex items-center gap-2 text-sm text-[#64748B] hover:text-[#2D3748] transition-colors">
              <Mail size={14} />
              {member.email}
            </a>
          )}
          {member.phone && (
            <a href={`tel:${member.phone}`} className="flex items-center gap-2 text-sm text-[#64748B] hover:text-[#2D3748] transition-colors">
              <Phone size={14} />
              {member.phone}
            </a>
          )}
        </div>
      </div>
    </div>
  );

  let cardIndex = 0;

  return (
    <div className="py-12">
      <SEO
        siteKey={siteKey}
        title="Náš tým"
        description={`Seznamte se s naším týmem v ${SITE_NAMES[siteKey]}. Zkušené a laskavé učitelky, pedagogické asistentky a další zaměstnanci, kteří s láskou pečují o vaše děti.`}
        keywords={['zaměstnanci', 'učitelky', 'tým', 'kolektiv', 'pedagogové']}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <Link to={`/${siteKey}`} className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#45AAF2] mb-4" data-testid="back-link">
            <ArrowLeft size={18} />
            Zpet na uvodní stránku
          </Link>
          <h1 className="text-4xl font-bold text-[#2D3748]" data-testid="page-title">Náš tým</h1>
          <p className="text-[#64748B] mt-2">Poznejte náš kolektiv</p>
        </div>

        {staff.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border-3 border-[#F0F4F8]">
            <User size={48} className="mx-auto text-[#CBD5E1] mb-4" />
            <div className="text-[#64748B]">Informace o týmu zatím nejsou k dispozici</div>
          </div>
        ) : (
          <div className="space-y-12">
            {Object.entries(staffByClass).map(([className, members]) => (
              <div key={className}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-1.5 h-8 rounded-full" style={{ backgroundColor: colors.primary }} />
                  <h2 className="text-2xl font-bold text-[#2D3748]">{className}</h2>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {members.map((member) => (
                    <StaffCard key={member.id} member={member} index={cardIndex++} />
                  ))}
                </div>
              </div>
            ))}
            
            {unassigned.length > 0 && (
              <div>
                {Object.keys(staffByClass).length > 0 && (
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-1.5 h-8 rounded-full bg-[#64748B]" />
                    <h2 className="text-2xl font-bold text-[#2D3748]">Vedení a další</h2>
                  </div>
                )}
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {unassigned.map((member) => (
                    <StaffCard key={member.id} member={member} index={cardIndex++} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StaffPage;
